import React, { useEffect, useState } from "react";
import { Row, Col, Space, Button, Tabs, Modal, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import PatientGalleryUploadOP from "./PatientGalleryUploadOP";
import AddLabelModalOP from "./AddLabelModalOP";
import PatientImageViewOP from "./PatientImageViewOP";
import useQuery from "../../../../hooks/useQuery";
import {
  getAdmissionApi,
  getFolderDataApi,
  getFoldersListApi,
  getRecycleBinUploadsApi,
  patchAdmissionApi,
} from "../../../../services/op.service";
import { getPatientExercisesAPI } from "../../../../services/outpatient.services";
import { folderHasUploads } from "../../../../helpers/galleryUtils";
import GalleryLabelTab from "../../../PatientsIP/PatientGallery/GalleryLabelTab";
import "../../../PatientsIP/PatientGallery/GalleryLabelTab.css";
import "../../../PatientsIP/PatientGallery/PatientImageView.css";
import "../../../OpEMR/Physiotherapist/Gallery/galleryOp.css";

const PatientGalleryOP = ({ patientData }) => {
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [labelModal, setLabelModal] = useState(false);
  const [refreshLabels, setRefreshLabels] = useState(false);
  const [galleryModal, setGalleryModal] = useState(false);
  const [activeKey, setActiveKey] = useState(0);
  const [fileList, setFileList] = useState([]);
  const params = useQuery();
  const [folders, setFolders] = useState([]);

  const refreshPage = () => setRefresh((prev) => !prev);
  const refreshLabelsPage = () => setRefreshLabels((prev) => !prev);
  const isRecycleBin = activeKey === "recycle_bin";

  const fetchFolderData = () => {
    if (activeKey === "exercises_op") {
      return getPatientExercisesAPI(params?.admission)
        .then((res) => setFileList(res?.data ?? []))
        .catch((err) => console.log(err));
    }
    if (activeKey === "recycle_bin") {
      return getRecycleBinUploadsApi(params?.admission)
        .then((res) => setFileList(res?.data ?? []))
        .catch((err) => console.log(err));
    }
    const folderKey = activeKey === 0 ? null : activeKey;
    return getFolderDataApi(folderKey, params?.admission)
      .then((res) => setFileList(res?.data ?? []))
      .catch((err) => console.log(err));
  };

  const handleTabChange = (value) => {
    setActiveKey(value);
    setLoading(true);
  };

  const handleTabEdit = (targetKey, action) => {
    if (action === "add") {
      setLabelModal(true);
    }
  };

  const confirmDeleteEmptyLabel = (folderName) => {
    Modal.confirm({
      title: `Delete label "${folderName}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const res = await getFolderDataApi(folderName, params?.admission);

          if (folderHasUploads(res?.data)) {
            message.error(
              "Cannot delete label with files. Remove or move files first."
            );
            return;
          }

          const updatedFolders = folders.filter((f) => f !== folderName);
          await patchAdmissionApi(params?.admission, {
            gallery_folders: updatedFolders,
            admission: params?.admission,
          });

          setFolders(updatedFolders);
          if (activeKey === folderName) {
            setActiveKey(0);
          }
          message.success("Label deleted successfully");
          refreshPage();
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to delete label"
          );
        }
      },
    });
  };

  const fetchFolders = async () => {
    getAdmissionApi(params?.admission)
      .then((res) => {
        setFolders([...new Set(res?.data?.gallery_folders ?? [])]);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    setLoading(true);
    fetchFolderData().finally(() => setLoading(false));
  }, [refresh, activeKey]);

  useEffect(() => {
    getFoldersListApi(params?.admission)
      .then(() => {})
      .catch((err) => console.log(err));
    fetchFolders();
  }, [refreshLabels]);

  return (
    <Row gutter={[12, 12]} className="patient-gallery op-gallery">
      <Col span={24} className="gallery-actions-col">
        <div className="gallery-actions-row">
          <Space size={[8, 8]} wrap={false}>
            {activeKey !== "recycle_bin" && (
              <Button type="primary" ghost onClick={() => setLabelModal(true)}>
                Add Label
              </Button>
            )}
            {!isRecycleBin && (
              <Button
                className="inv-btn"
                type="primary"
                onClick={() => setGalleryModal(true)}
                disabled={activeKey === "exercises_op"}
              >
                <UploadOutlined />
                Upload
              </Button>
            )}
          </Space>
        </div>
      </Col>

      <Row
        gutter={0}
        className="op-gallery-panel"
        style={{
          width: "100%",
          background: "#fff",
          borderRadius: 10,
        }}
      >
        <Col span={24}>
          <Tabs
            className="gallery-tabs patient-gallery-tabs"
            type="editable-card"
            activeKey={activeKey}
            onChange={handleTabChange}
            onEdit={handleTabEdit}
            items={[
              { label: "All", key: 0, closable: false },
              ...folders.map((item) => ({
                label: (
                  <GalleryLabelTab
                    name={item}
                    isSelected={activeKey === item}
                    isEmpty={!loading && !folderHasUploads(fileList)}
                    canDelete
                    onDelete={() => confirmDeleteEmptyLabel(item)}
                  />
                ),
                key: item,
                closable: false,
              })),
              { label: "Exercises", key: "exercises_op", closable: false },
              { label: "Recycle Bin", key: "recycle_bin", closable: false },
            ]}
            style={{
              background: "white",
              display: "block",
              padding: "5px",
              width: "100%",
            }}
          />
        </Col>

        <Col span={24} style={{ width: "100%", background: "#fff" }}>
          <PatientImageViewOP
            data={fileList}
            isLoading={loading}
            setLoading={setLoading}
            exercises={activeKey === "exercises_op"}
            activeKey={activeKey}
            refresh={refreshPage}
          />
        </Col>
      </Row>

      {labelModal && (
        <AddLabelModalOP
          visible={labelModal}
          onClose={() => {
            setLabelModal(false);
            fetchFolderData();
          }}
          onAdded={() => refreshLabelsPage()}
          labels={folders}
        />
      )}

      {galleryModal && (
        <PatientGalleryUploadOP
          refresh={refreshPage}
          isModalVisible={galleryModal}
          setIsModalVisible={setGalleryModal}
          folder={activeKey}
          refreshLabelsPage={refreshLabelsPage}
          patientData={patientData}
          labels={folders}
        />
      )}
    </Row>
  );
};

export default PatientGalleryOP;